import type { Result } from 'neverthrow';

import { apiRequest, type ApiError } from './client';
import type { ShipFromAddress } from './ship-from-addresses';
import type { BcConnection } from './connections';
import type { CarrierConnectionSummary } from './carrier-connections';

/**
 * Tenant as the SPA sees it. Mirrors `@mailchute/db/tenants` `TenantSchema`
 * but kept as a manual interface here so the frontend package stays
 * dependency-free of the backend Lambda packages — the wire format is
 * the contract.
 *
 * When `default_ship_from_address_id` is set, `GET /tenants/me`
 * embeds the address inline as `default_ship_from_address` so step 2
 * can pre-fill without a second round trip.
 */
export interface MyTenant {
  readonly tenant_id: string;
  readonly created_at: string;
  readonly legal_name?: string;
  readonly trading_name?: string;
  readonly currency?: 'GBP' | 'USD' | 'EUR';
  readonly country?: string;
  readonly default_ship_from_address_id?: string;
  readonly default_ship_from_address?: ShipFromAddress;
  /** Active BigCommerce connection metadata (sanitised — no token). */
  readonly bc_connection?: BcConnection;
  /**
   * Active carrier connections (sanitised — no credentials). Always
   * present (empty array when the tenant has none). The wizard's
   * step 4 card grid renders one tile per `CARRIER_KIND` and merges
   * status against this list.
   */
  readonly carrier_connections: readonly CarrierConnectionSummary[];
  /**
   * Billing state from the Stripe Subscription (ADR 0017). Absent until
   * Step 5 creates the trial Subscription. `trial_ends_at` is the ISO
   * datetime the 7-day trial converts to paid. Surfaced read-only on
   * the Settings billing card.
   */
  readonly billing_status?: 'trial' | 'active' | 'past_due' | 'cancelled' | 'unbilled';
  readonly trial_ends_at?: string;
  /** ISO datetime; non-null means onboarding is complete. */
  readonly onboarded_at?: string;
}

/**
 * Partial-update shape sent to `PATCH /tenants/me`. Each field optional;
 * only the supplied keys get written. `onboarded_at` is intentionally
 * absent — that's a server-controlled state transition with its own
 * route (`POST /tenants/me/onboard`) so the client can't forge the
 * timestamp.
 */
export interface TenantUpdate {
  readonly legal_name?: string;
  readonly trading_name?: string;
  readonly currency?: 'GBP' | 'USD' | 'EUR';
  readonly country?: string;
  readonly default_ship_from_address_id?: string;
}

export function getMyTenant(): Promise<Result<MyTenant, ApiError>> {
  return apiRequest<MyTenant>({ method: 'GET', path: '/tenants/me' });
}

export function updateMyTenant(updates: TenantUpdate): Promise<Result<MyTenant, ApiError>> {
  return apiRequest<MyTenant>({ method: 'PATCH', path: '/tenants/me', body: updates });
}

/**
 * Mark the caller's tenant as onboarded. Called from Step 5's Finish
 * button. Server controls the timestamp — no body. The response is
 * the full updated tenant; cache invalidation is the caller's job.
 */
export function onboardMyTenant(): Promise<Result<MyTenant, ApiError>> {
  return apiRequest<MyTenant>({ method: 'POST', path: '/tenants/me/onboard' });
}

/**
 * Query key shared by the loader, the form, and any mutation that needs
 * to invalidate. Used through `queryClient.ensureQueryData` (in route
 * loaders) and `useSuspenseQuery` (in components) per apps/web/CLAUDE.md
 * §5.
 */
export const TENANT_ME_QUERY_KEY = ['tenant', 'me'] as const;
